import type { SalarySumScope } from '../salary-month-summary-cards'

interface SalaryCodeLine<T> {
  id: string | null
  name: string | null
  salaryId: string | null
  sum: T | null
}

export interface ScopedSalaryCodeRow<T> {
  id: string | null
  name: string | null
  salaryId: string | null
  all: T | null
  approved: T | null
  /** The scope this row's sum was not calculated for, if any. */
  notCalculated: SalarySumScope | null
}

/**
 * Pair each salary code's "all" line with its "approved" one, in the order of
 * `allLines`. The backend keys month lines by name, so they are matched on it.
 * Approving only removes hours, so every approved code is also an "all" code;
 * a code missing from `approvedLines` has nothing approved yet.
 *
 * Codes in `payrollScopedSalaryIds` carry payroll's sum in both scopes, so the
 * scope payroll does not use is marked as not calculated.
 */
export function getScopedSalaryCodeRows<T>(
  allLines: SalaryCodeLine<T>[],
  approvedLines: SalaryCodeLine<T>[],
  payrollScopedSalaryIds: string[],
  payrollScope: SalarySumScope
): ScopedSalaryCodeRow<T>[] {
  const approvedByName = new Map(
    approvedLines.map(line => [line.name, line.sum])
  )
  const payrollScoped = new Set(payrollScopedSalaryIds)
  const otherScope: SalarySumScope = payrollScope === 'all' ? 'approved' : 'all'

  return allLines.map(line => ({
    id: line.id,
    name: line.name,
    salaryId: line.salaryId,
    all: line.sum,
    approved: approvedByName.get(line.name) ?? null,
    notCalculated:
      line.salaryId && payrollScoped.has(line.salaryId) ? otherScope : null,
  }))
}
